import type {Repository} from './repo'

/** Loader shape the repo/file pickers call to fetch repositories for a query. */
export type RepositoryLoader = (query: string, signal?: AbortSignal) => Promise<Repository[]>

/** Owner login parsed from a `owner/repo` repository name. */
export function repositoryOwner(repo: Repository): string {
  return repo.name.split('/')[0] ?? ''
}

/**
 * Returns true when a repository is valid under the active scope owner.
 *
 * With an undefined scope owner every repository is in scope; null or empty
 * means an unresolved owner and excludes all repositories. With a scope owner only
 * repositories owned by that account (case-insensitive) remain — both public and
 * private repos owned by anyone else are dropped. This mirrors the environment
 * strip's `isRepoInScope` so the attachment pickers cannot offer repositories the
 * selected scope excludes.
 */
export function isRepositoryInScope(repo: Repository, scopeOwner: string | null | undefined): boolean {
  if (scopeOwner === undefined) return true
  if (!scopeOwner) return false
  return repositoryOwner(repo).toLowerCase() === scopeOwner.toLowerCase()
}

/**
 * Prefixes a search query with the scope owner so the backend can prioritize
 * in-scope repositories. Queries that already target an owner (contain `/`) are
 * left untouched.
 *
 * A blank query is also left untouched. Both repository endpoints treat a blank
 * query as "return the viewer's top repositories", ranked by recent activity;
 * prefixing turns it into an owner listing, which is ordered arbitrarily and
 * ignores the pre-selected repository. Scope is enforced by filtering results,
 * not by rewriting a blank query. Whitespace-only input counts as blank because
 * the backend trims before matching, so `<owner>/   ` would reach that same
 * owner-listing branch.
 */
export function scopedRepositoryQuery(query: string, scopeOwner: string | null | undefined): string {
  if (!query.trim()) return query
  return scopeOwner && !query.includes('/') ? `${scopeOwner}/${query}` : query
}

/**
 * Wraps a repository loader so results are restricted to the active scope owner.
 *
 * When `scopeOwner` is set the wrapped loader scopes the query and filters the
 * results to that owner; the client-side filter is the authoritative guard so an
 * out-of-scope repository can never be attached even if the backend returns one.
 * When `scopeOwner` is undefined the original loader is returned unchanged.
 * A null or empty owner blocks searching until the scoped owner resolves.
 */
export function createScopedRepositoryLoader(
  loader: RepositoryLoader,
  scopeOwner: string | null | undefined,
): RepositoryLoader {
  if (scopeOwner === undefined) return loader
  if (!scopeOwner) return async () => []
  return async (query, signal) => {
    const repos = await loader(scopedRepositoryQuery(query, scopeOwner), signal)
    return repos.filter(repo => isRepositoryInScope(repo, scopeOwner))
  }
}
